import React from 'react';
import { observer } from 'mobx-react';
import { useParams } from 'react-router-dom';

import { CircularProgress } from '@mui/material';

import { CenteredCircularProgress } from 'components/Progress';

import { useStore } from 'utils/hooks/store';

import { MainContainer, ItemContainer } from 'containers/Notifications/components/NotificationsList/styles';
import EmptyInboxContainer from 'containers/Notifications/components/EmptyInboxContainer';
import KnockNotificationItem from 'containers/KnockNotifications/components/KnockNotificationItem';
import { NotificationCategory, CATEGORY_CONFIG, CATEGORY_LIST } from 'containers/KnockNotifications/constants';

import { DayTitle } from './styles';

const msToDays = (time: number): number => Math.floor(time / 1000 / 60 / 60 / 24);

const getEmptyState = (
  searchQuery: string,
  showUnreadOnly: boolean,
  activeCategory: NotificationCategory
): { title: string; description: string } => {
  if (searchQuery) {
    return {
      title: 'No notifications match your search',
      description: 'Clear your search to see all notifications.',
    };
  }
  if (showUnreadOnly) {
    return {
      title: "You're all caught up",
      description: 'You have read everything. New notifications will appear here.',
    };
  }
  if (activeCategory !== NotificationCategory.ALL) {
    const noun = CATEGORY_CONFIG[activeCategory].label.toLowerCase();
    const article = /^[aeiou]/.test(noun) ? 'an' : 'a';
    return {
      title: `No ${noun} notifications`,
      description: `When there is an update on ${article} ${noun}, you will see it here.`,
    };
  }
  return { title: 'No notifications yet', description: 'When something needs your attention, it will show up here.' };
};

const KnockNotificationsList: React.FunctionComponent = (): React.ReactElement => {
  const { category } = useParams<{ category?: string }>();
  const {
    dualInbox: {
      initialSync,
      loadInitialData,
      filteredNotifications,
      isLoading,
      loadNotifications,
      setActiveCategory,
      searchQuery,
      showUnreadOnly,
      activeCategory,
    },
  } = useStore();
  const containerRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    const matched = CATEGORY_LIST.find((c) => c === category);
    setActiveCategory(matched ?? NotificationCategory.ALL);
  }, [category]);

  const handleScroll = (): void => {
    const container = containerRef.current;
    if (container) {
      const containerHeight = container.offsetHeight;
      const scrollY = window.innerHeight + window.scrollY;
      if (scrollY >= containerHeight) {
        loadNotifications();
      }
    }
  };

  React.useEffect(() => {
    if (initialSync === undefined) {
      loadInitialData();
    }
  }, []);

  React.useEffect(() => {
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  if (!initialSync && isLoading) {
    return <CenteredCircularProgress data-testid="loading" />;
  }

  if (filteredNotifications === undefined || filteredNotifications.length === 0) {
    const { title, description } = getEmptyState(searchQuery, showUnreadOnly, activeCategory);
    return <EmptyInboxContainer title={title} description={description} />;
  }

  const sections = [
    {
      title: 'Today',
      data: filteredNotifications.filter(
        (item) => msToDays(new Date().getTime() - new Date(item.created).getTime()) === 0
      ),
    },
    {
      title: 'Yesterday',
      data: filteredNotifications.filter(
        (item) => msToDays(new Date().getTime() - new Date(item.created).getTime()) === 1
      ),
    },
    {
      title: 'Past 7 days',
      data: filteredNotifications.filter((item) => {
        const daysPassed = msToDays(new Date().getTime() - new Date(item.created).getTime());
        return daysPassed > 1 && daysPassed <= 7;
      }),
    },
    {
      title: 'Earlier',
      data: filteredNotifications.filter(
        (item) => msToDays(new Date().getTime() - new Date(item.created).getTime()) > 7
      ),
    },
  ];

  return (
    <MainContainer ref={containerRef} data-testid="knock-notification-list">
      {sections.map(({ title, data }) => (
        <div key={`${title}-${data.length}`}>
          {data.length > 0 ? <DayTitle key={`title-${title}`}>{title}</DayTitle> : null}
          {data.map((i) => (
            <KnockNotificationItem key={i.uuid} item={i} />
          ))}
        </div>
      ))}
      {isLoading ? (
        <ItemContainer>
          <CircularProgress />
        </ItemContainer>
      ) : null}
    </MainContainer>
  );
};

export default observer(KnockNotificationsList);
